import React, { ReactElement } from 'react';

import { Box, Card, CardContent, Grid, Typography } from '@mui/material';

import { Province } from '../../@types/KingdomResponse';
import {
  removeDecimalsToNumber,
  numberToStringWithCommas,
  removeDecimalsToString,
} from '../../helpers/NumberFormatting';
import { ReactComponent as AcresSVG } from '../../svgs/others/asset-acres.svg';
import { ReactComponent as BeSVG } from '../../svgs/others/asset-be.svg';
import { ReactComponent as FoodSVG } from '../../svgs/others/asset-food.svg';
import { ReactComponent as GoldSVG } from '../../svgs/others/asset-gold-coins.svg';
import { ReactComponent as NetSVG } from '../../svgs/others/asset-net-worth.svg';
import { ReactComponent as PopSVG } from '../../svgs/others/asset-population.svg';
import { ReactComponent as RunesSVG } from '../../svgs/others/asset-runes.svg';
import { ReactComponent as DefSpecSVG } from '../../svgs/unit/unit-defensive-specialists.svg';
import { ReactComponent as OffSpecSVG } from '../../svgs/unit/unit-offensive-specialists.svg';
import { ReactComponent as SoldiersSVG } from '../../svgs/unit/unit-soldiers.svg';
import { ReactComponent as ThievesSVG } from '../../svgs/unit/unit-thieves.svg';
export const svgProps = { style: { height: 16, width: 20 } };

const hidePA = true;
const showPA = false;
const svgStyles = { width: '30px', height: '30px', marginRight: '5px' };
interface Props {
  rows: Province[];
}

const getCardItem = (
  header: string,
  value: string,
  average: string,
  perAcre: number | undefined,
  svg?: ReactElement
) => {
  return (
    <Grid
      size={{
        xs: 4,
        sm: 3,
        md: 2,
        lg: 2,
        xl: 1
      }}>
      <Card sx={{ height: '120px' }}>
        <CardContent sx={{ paddingTop: 1, paddingBottom: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
            }}
          >
            {svg}
            <Typography variant='subtitle2' textAlign='center'>
              {header}
            </Typography>
          </Box>
          {value && (
            <Typography variant='body2' textAlign='center'>
              Tot: {value}
            </Typography>
          )}
          {average && (
            <Typography variant='body2' textAlign='center'>
              Avg: {average}
            </Typography>
          )}
          {perAcre ? (
            <Typography variant='body2' textAlign='center'>
              p/a: {perAcre}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    </Grid>
  );
};

const Stats: React.FC<Props> = ({ rows }) => {
  const getNumberOfRowsContainingData = (intelType: any, field: string) => {
    return rows.filter((r) => r[intelType] && r[intelType][field]).length;
  };

  const getAverage = (intelType: string, field: string) => {
    const numberOfRows =
      intelType === 'province'
        ? rows.length
        : getNumberOfRowsContainingData(intelType, field);
    const sum = getTotal(intelType, field);
    return sum / numberOfRows;
  };

  const reduceField = (intelType: string, field: string, rows: Province[]) => {
    const reducer = (total: number, r: Province) => {
      const val =
        r && r[intelType] && r[intelType][field] ? r[intelType][field] : 0;
      return total + val;
    };
    return rows.reduce(reducer, 0);
  };

  const reduceProvince = (field: string, rows: Province[]) => {
    const reducer = (total: number, r: Province) => {
      const val = r && r[field] ? r[field] : 0;
      return total + val;
    };
    return rows.reduce(reducer, 0);
  };

  const getTotal = (intelType: string, field: string) => {
    let sum = 0;
    if (intelType === 'province') {
      sum = reduceProvince(field, rows);
    } else {
      sum = reduceField(intelType, field, rows);
    }

    return sum;
  };

  const getMilitary = () => {
    let sum = 0;
    sum = rows.reduce(
      (a: number, b: Province) =>
        a + (b.sot ? b.sot.oSpecs + b.sot.dSpecs + b.sot.elites : 0),
      0
    );
    return sum;
  };

  const getTPAStatWithAverage = (label: string, svg?: ReactElement) => {
    const rowsWithThieves = rows.filter((p) => p.sot && p.sot.thieves > 0);
    const totalLandWithThieves = reduceField('sot', 'land', rowsWithThieves);
    const total: number = getTotal('sot', 'thieves');
    const avg: number = removeDecimalsToNumber(total / rowsWithThieves.length);
    const perAcre: number | undefined = removeDecimalsToNumber(
      total / totalLandWithThieves
    );
    return getCardItem(
      label,
      numberToStringWithCommas(total),
      numberToStringWithCommas(avg),
      perAcre,
      svg
    );
  };

  const getMilitaryWithAverage = (hidePerAcre: boolean, svg?: ReactElement) => {
    const total: number = getMilitary();
    const perAcre: number | undefined = hidePerAcre
      ? undefined
      : removeDecimalsToNumber(total / totalLand);
    return getCardItem(
      'Units',
      numberToStringWithCommas(total),
      '',
      perAcre,
      svg
    );
  };

  const getStatWithAverage = (
    intelType: string,
    field: string,
    label: string,
    hidePerAcre: boolean,
    svg?: ReactElement
  ) => {
    const total: number = getTotal(intelType, field);
    const avg: number = removeDecimalsToNumber(getAverage(intelType, field), 0);
    const perAcre: number | undefined = hidePerAcre
      ? undefined
      : removeDecimalsToNumber(total / totalLand);
    return getCardItem(
      label,
      numberToStringWithCommas(total),
      numberToStringWithCommas(avg),
      perAcre,
      svg
    );
  };

  const getAveragePercentage = (intelType: string, field: string) => {
    const average: number =
      getTotal(intelType, field) /
      getNumberOfRowsContainingData(intelType, field);
    return `${removeDecimalsToString(average)}%`;
  };

  const totalLand = getTotal('sot', 'land');

  return (
    <Box>
      <Grid container spacing={1} sx={{ flexGrow: 1 }} alignItems='stretch'>
        {getStatWithAverage(
          'province',
          'land',
          'Land',
          hidePA,
          <AcresSVG style={{ ...svgStyles, fill: 'MediumSeaGreen' }} />
        )}
        {getStatWithAverage(
          'province',
          'networth',
          'NW',
          showPA,
          <NetSVG style={{ ...svgStyles, fill: 'gold' }} />
        )}
        {getStatWithAverage(
          'sot',
          'offPoints',
          'Off',
          showPA,
          <OffSpecSVG style={{ ...svgStyles, fill: 'silver' }} />
        )}
        {getStatWithAverage(
          'sot',
          'defPoints',
          'Def',
          showPA,
          <DefSpecSVG style={{ ...svgStyles, fill: 'LimeGreen' }} />
        )}
        {getStatWithAverage(
          'sot',
          'peasants',
          'Peons',
          showPA,
          <PopSVG style={{ ...svgStyles, fill: 'tan' }} />
        )}
        {getMilitaryWithAverage(
          showPA,
          <PopSVG style={{ ...svgStyles, fill: 'tan' }} />
        )}
        {getStatWithAverage(
          'sot',
          'soldiers',
          'Solds',
          showPA,
          <SoldiersSVG style={{ ...svgStyles, fill: 'gold' }} />
        )}
        {getStatWithAverage(
          'sot',
          'money',
          'GCs',
          showPA,
          <GoldSVG style={{ ...svgStyles, fill: 'gold' }} />
        )}
        {getStatWithAverage(
          'sot',
          'runes',
          'Runes',
          showPA,
          <RunesSVG style={{ ...svgStyles, fill: 'orange' }} />
        )}
        {getStatWithAverage(
          'sot',
          'food',
          'Food',
          showPA,
          <FoodSVG style={{ ...svgStyles, fill: 'Khaki' }} />
        )}
        {getTPAStatWithAverage(
          'TPA',
          <ThievesSVG style={{ ...svgStyles, fill: 'gold' }} />
        )}
        {/* {getStatWithAverage(
          'sot',
          'wizards',
          'WPA',
          showPA,
          <WizardsSVG style={{ ...svgStyles, fill: 'gold' }} />
        )} */}
        <Grid
          size={{
            xs: 4,
            sm: 3,
            md: 2,
            lg: 2,
            xl: 1
          }}>
          <Card sx={{ height: '120px' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'row',
                }}
              >
                <BeSVG style={{ ...svgStyles, fill: 'blue' }} />
                <Typography variant='subtitle2' textAlign='center' mb={1}>
                  Efficiency
                </Typography>
              </Box>
              <Typography variant='body2' textAlign='center'>
                BE: {getAveragePercentage('sot', 'be')}
              </Typography>

              <Typography variant='body2' textAlign='center'>
                OME: {getAveragePercentage('som', 'ome')}
              </Typography>

              <Typography variant='body2' textAlign='center'>
                DME: {getAveragePercentage('som', 'dme')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Stats;
